/**
 * Calendar job-type roll-up label: "Refinish" | "Maintenance" | null.
 *
 * Rendered under the price line on calendar event blocks in DayModal, WeekModal,
 * and DayViewModal. Single source of truth — these three modals previously each
 * carried a byte-identical local `getJobTypeLabel` copy.
 *
 * Surcharge / parts lines (Trip Charge, Tip, Hardware Change, Other parts) are
 * EXCLUDED from the Refinish-vs-Maintenance decision — they are not job work and
 * must not influence the category. A Trip Charge that carries a stray
 * jobType/payBucket "Refinish" was making Maintenance jobs display as "Refinish"
 * (Invoice #21078, 2026-06-02).
 *
 * Detection is by itemName / jobType ONLY — deliberately NOT via the pay-bucket
 * chain (payBucketsClient.bucketForItem), because that consults `payBucket` first
 * and a legacy surcharge line can carry a stray payBucket "refinish"/"half" that
 * would sneak it back into the decision. Name/jobType detection is immune to that.
 *
 * Rules (locked 2026-06-02):
 *  - Non-job events (go_by, vacation): null (shown via name prefix instead).
 *  - No real work items after excluding surcharges: null (blank).
 *  - Any real work item with jobType "Refinish": "Refinish" (Refinish wins).
 *  - Otherwise: "Maintenance".
 */

// jobType values the catalog assigns to non-job-work lines (modern items).
const SURCHARGE_JOBTYPES = new Set([
  "Trip Charge",
  "Tip",
  "Hardware Change",
  "Other",
]);

// True for surcharge / parts lines that must not influence the job category.
// Name-based fallbacks cover legacy lines created before jobType tagging existed.
function isSurchargeOrParts(item) {
  if (!item) return true; // nothing to categorize from a null line
  const name = (item.itemName || "").trim();
  const jobType = (item.jobType || "").trim();
  if (SURCHARGE_JOBTYPES.has(jobType)) return true;
  if (name.toLowerCase() === "tip") return true;
  if (/trip\s*charge/i.test(name)) return true; // the reported-bug culprit
  if (/hardware change/i.test(name)) return true;
  if (/weather\s*strip/i.test(name) || /door\s*sweep/i.test(name)) return true;
  return false;
}

export function getCalendarJobCategory(item) {
  const et = item?.calendarItem?.eventType || "job";
  if (et !== "job") return null;
  const lineItems = [
    ...(item?.invoice?.invoiceItems || []),
    ...(item?.quote?.quoteItems || []),
  ];
  const realWork = lineItems.filter((i) => !isSurchargeOrParts(i));
  if (realWork.length === 0) return null;
  return realWork.some((i) => i?.jobType === "Refinish")
    ? "Refinish"
    : "Maintenance";
}
